/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { ColorIcon } from "@/icons";
import { SketchPicker } from "react-color";
import {
  saveUserSettings,
  User,
  UserSettings,
} from "@/database/userDatabaseService";
import "./index.scss";
import { EraserShortCuts, PageItemPosition } from "@/types";
import ButtonGroup from "./ButtonGroup";
import Button from "@/components/Button";
import {
  getContinueLastPageOptions,
  getEraserOptions,
  getHideIconsOptions,
  getPageItemPositionOptions,
  getPageNavigationOptions,
  getSaveDrawingOptions,
  getToolBoxAutoMoveOptions,
} from "./types";
import { toast } from "react-toastify";
import { AppDispatch } from "@/redux-store";
import { useModal } from "@/contexts/ModalContext";
import { CustomizeToolBox } from "./CustomizeToolBox";

interface UserSettingsProps {
  user: User;
  closeSideBar: () => void;
  dispatch: AppDispatch;
}
const Settings: React.FC<UserSettingsProps> = ({
  user,
  closeSideBar,
  dispatch,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'settings' });
  const [colors, setColors] = useState<string[]>([]);
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false);
  const [colorIndex, setColorIndex] = useState(0);
  const [pickerColor, setPickerColor] = useState<string>("");
  const [pageItemPosition, setPageItemPosition] = useState<PageItemPosition>();
  const [showNextAreaOnPageNavigation, setShowNextAreaOnPageNavigation] =
    useState<boolean>(false);
  const [eraserShortCut, setEraserShortCut] = useState<EraserShortCuts>();
  const [saveDrawingsOption, setSaveDrawings] = useState<boolean>(true);
  const [continueLastPage, setContinueLastPage] = useState<boolean>(true);
  const [hideIcons, setHideIcons] = useState<boolean>(false);
  const [isToolsAutoMoving, setIsToolsAutoMoving] = useState<boolean>(false);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);

  const { openModal, closeModal } = useModal();
  useEffect(() => {
    const settings = user?.userSettings;
    if (settings) {
      const {
        colors,
        pageItemPosition,
        showNextAreaOnPageNavigation,
        eraserShortCut,
        saveDrawings,
        continueLastPage,
        hideIcons,
        isToolsAutoMoving,
        selectedTools,
      } = settings;

      setColors(colors || []);
      setPageItemPosition(pageItemPosition);
      setShowNextAreaOnPageNavigation(showNextAreaOnPageNavigation);
      setEraserShortCut(eraserShortCut);
      setSaveDrawings(saveDrawings);
      setContinueLastPage(continueLastPage);
      setHideIcons(hideIcons);
      setIsToolsAutoMoving(isToolsAutoMoving || false);
      setSelectedTools(selectedTools || []);
    }
  }, [user]);

  const renderColors = () => {
    return colors.map((color, index) => {
      return (
        <button
          key={`color-${index}`}
          className="obro-transparent-btn"
          onClick={() => openColorPicker(index, color)}
        >
          <ColorIcon color={color} size={35} className={undefined} />
        </button>
      );
    });
  };
  const openColorPicker = (index: number, color: string) => {
    setShowColorPicker(!showColorPicker);
    setColorIndex(index);
    setPickerColor(color);
  };
  const updateColors = (color: string) => {
    const newColors: string[] = [...colors];
    newColors[colorIndex] = color;
    setColors(newColors);
    setPickerColor(color);
  };

  const saveSettings = async () => {
    const settings: UserSettings = {
      showContentInfo: true,
      saveDrawings: saveDrawingsOption,
      continueLastPage: continueLastPage,
      pageItemPosition: pageItemPosition || PageItemPosition.left,
      pageItemIsFull: false,
      colors: colors,
      hideIcons: hideIcons,
      isToolsAutoMoving: isToolsAutoMoving,
      eraserShortCut: eraserShortCut || EraserShortCuts.Closed,
      selectedTools: selectedTools,
      showNextAreaOnPageNavigation: showNextAreaOnPageNavigation,
      userId: user.id || "",
      toolBoxSize: user.userSettings?.toolBoxSize || 1,
      longPressTime: user.userSettings?.longPressTime || 500,
      thickness: user.userSettings?.thickness || [3, 5, 7, 8],
    };
    console.log(settings);
    saveUserSettings(user.id, settings).then(() => {
      // dispatch(setUser({ ...user, userSettings: settings }));
      closeSideBar();
    });

    toast.success(t("settings_saved"));
  };

  const openCustomizeToolBox = () => {
    closeSideBar();
    openModal(
      "test",
      t("customize_toolbox"),
      "withOverlay",
      <CustomizeToolBox
        userId={user.id}
        settings={user.userSettings}
        userTools={selectedTools}
        closeModal={closeModal}
        dispatch={dispatch}
      />
    );
  };
  return (
    <>
      <div className="settings-content">
        <div className="setting">
          <div className="setting-type-text">
            <span>{t("selectable_colors")}</span>
          </div>
          <div className="setting-colors">{renderColors()}</div>
          {showColorPicker ? (
            <div style={{ position: "absolute", zIndex: 2 }}>
              <div
                style={{
                  position: "fixed",
                  top: "0px",
                  right: "0px",
                  bottom: "0px",
                  left: "0px",
                }}
                onClick={() => setShowColorPicker(!showColorPicker)}
              />
              <SketchPicker
                color={pickerColor}
                onChangeComplete={(color: any) => updateColors(color.hex)}
              />
            </div>
          ) : null}
        </div>
        <div className="setting">
          <div className="setting-type-text">
            <span>{t("content_position")}</span>
          </div>
          <ButtonGroup
            items={getPageItemPositionOptions(t)}
            selected={pageItemPosition}
            onSelect={setPageItemPosition}
            buttonClassName="w-33"
          />
        </div>
        <div className="setting">
          <div className="setting-type-text">
            <span>{t("page_navigation_event")}</span>
          </div>
          <ButtonGroup
            items={getPageNavigationOptions(t)}
            selected={showNextAreaOnPageNavigation}
            onSelect={setShowNextAreaOnPageNavigation}
            buttonClassName="w-33"
          />
        </div>
        <div className="setting">
          <div className="setting-type-text">
            <span>{t("eraser_shortcut")}</span>
          </div>
          <ButtonGroup
            items={getEraserOptions(t)}
            selected={eraserShortCut}
            onSelect={setEraserShortCut}
            buttonClassName="w-33"
          />
        </div>
        <div className="setting">
          <div className="setting-type-text">
            <span>{t("drawings_save_mode")}</span>
          </div>
          <ButtonGroup
            items={getSaveDrawingOptions(t)}
            selected={saveDrawingsOption}
            onSelect={(value) => {
              console.log(value);
              setSaveDrawings(value);
            }}
            buttonClassName="w-33"
          />
        </div>
        <div className="setting">
          <div className="setting-type-text">
            <span>{t("continue_from_last_page")}</span>
          </div>
          <ButtonGroup
            items={getContinueLastPageOptions(t)}
            selected={continueLastPage}
            onSelect={setContinueLastPage}
            buttonClassName="w-33"
          />
        </div>
        <div className="setting">
          <div className="setting-type-text">
            <span>{t("hide_icons")}</span>
          </div>
          <ButtonGroup
            items={getHideIconsOptions(t)}
            selected={hideIcons}
            onSelect={setHideIcons}
            buttonClassName="w-33"
          />
        </div>
        <div className="setting">
          <div className="setting-type-text">
            <span>{t("isToolBoxAutoMoving")}</span>
          </div>
          <ButtonGroup
            items={getToolBoxAutoMoveOptions(t)}
            selected={isToolsAutoMoving}
            onSelect={setIsToolsAutoMoving}
            buttonClassName="w-33"
          />
        </div>
        <div className="setting">
          <div className="setting-type-text">
            <span>{t("customize_toolbox")}</span>
          </div>
          <button
            className={`obro-btn obro-light-gray-btn full-w`}
            onClick={() => openCustomizeToolBox()}
          >
            <span>{t("customize_toolbox")}</span>
          </button>
        </div>

        <div className="flex">
          <Button
            className="obro-danger-outline-btn margin-right-10 rounded-btn"
            onClick={() => closeSideBar()}
          >
            {t("cancel")}
          </Button>
          <Button
            className="obro-secondary-btn rounded-btn"
            onClick={() => saveSettings()}
          >
            {t("save")}
          </Button>
        </div>
      </div>
    </>
  );
};

export default Settings;
